import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Siren, ArrowRight, Clock, AlertTriangle, Hammer, PackageCheck, Zap, CalendarClock, CalendarDays } from "lucide-react";
import { Events } from "@/lib/analytics";

const SCENARIOS = [
  { key: "cleaners-fell-short", icon: AlertTriangle, t: "Previous cleaners fell short" },
  { key: "move-in-deadline", icon: Clock, t: "Move-in deadline is imminent" },
  { key: "contractor-mess", icon: Hammer, t: "Contractor left it dirty" },
  { key: "last-minute-handover", icon: PackageCheck, t: "Last-minute property handover" },
];

const URGENCY = [
  { key: "today", icon: Zap, t: "Today", d: "Need someone now" },
  { key: "24h", icon: Clock, t: "Within 24h", d: "By tomorrow" },
  { key: "this-week", icon: CalendarClock, t: "This week", d: "In the next few days" },
  { key: "flexible", icon: CalendarDays, t: "Flexible", d: "Soon, but no fixed date" },
];

// Distinct urgent entry point. Lets the visitor set urgency + situation, then routes
// into the EXISTING contact/quote flow (?intent=rescue) with those choices prefilled.
export const AscendiaRescue = () => {
  const nav = useNavigate();
  const [urgency, setUrgency] = useState("24h");
  const [scenario, setScenario] = useState(null);

  const go = () => {
    Events.rescueStarted({ urgency, scenario: scenario || undefined });
    const params = new URLSearchParams({ intent: "rescue", urgency });
    if (scenario) params.set("scenario", scenario);
    nav(`/contact?${params.toString()}`);
  };

  return (
    <div data-testid="ascendia-rescue" className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-8 sm:p-10">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-amber-200">
            <Siren className="h-3.5 w-3.5" /> Ascendia Rescue
          </div>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-white u-tracking-tight">In a bind? We do urgent rescues.</h2>
          <p className="mt-3 text-secondary u-body max-w-xl">When timing is tight or a job was left unfinished, we step in fast with a clear scope and an honest assessment.</p>

          {/* Urgency selector */}
          <p className="mt-6 mb-2 text-[11px] uppercase tracking-[0.18em] text-amber-200/80">How soon do you need us?</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="How soon do you need us">
            {URGENCY.map((u) => {
              const on = urgency === u.key;
              return (
                <button key={u.key} type="button" role="radio" aria-checked={on}
                  onClick={() => setUrgency(u.key)}
                  data-testid={`rescue-urgency-${u.key}`}
                  className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${on ? "border-amber-400/60 bg-amber-400/15" : "border-[color:var(--border-subtle)] bg-black/30 hover:bg-white/[0.04]"}`}>
                  <u.icon className={`h-4 w-4 ${on ? "text-amber-300" : "text-amber-300/70"}`} strokeWidth={1.7} />
                  <span className={`text-sm font-medium ${on ? "text-white" : "text-secondary"}`}>{u.t}</span>
                  <span className="text-[10px] text-muted-c">{u.d}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button onClick={go} data-testid="rescue-cta-button" className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-6 h-11 text-sm font-semibold text-black hover:bg-amber-300 active:scale-[0.98] transition-[background-color,transform] duration-150">
              Request Urgent Assessment <ArrowRight className="h-4 w-4" />
            </button>
            <span className="text-xs text-muted-c">Availability &amp; pricing to be confirmed</span>
          </div>
        </div>

        {/* Scenario selector */}
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-amber-200/80">What's the situation? <span className="text-muted-c normal-case tracking-normal">(optional)</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SCENARIOS.map((s) => {
              const on = scenario === s.key;
              return (
                <button key={s.key} type="button" aria-pressed={on}
                  onClick={() => setScenario((p) => (p === s.key ? null : s.key))}
                  data-testid={`rescue-scenario-${s.key}`}
                  className={`flex items-start gap-2.5 rounded-xl border p-4 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${on ? "border-amber-400/60 bg-amber-400/15" : "border-[color:var(--border-subtle)] bg-black/30 hover:bg-white/[0.04]"}`}>
                  <s.icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${on ? "text-amber-300" : "text-amber-300/70"}`} strokeWidth={1.6} />
                  <span className={`text-sm ${on ? "text-white" : "text-secondary"}`}>{s.t}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AscendiaRescue;
